// Places the injected "Technische Anlagen" section directly above "Versicherungen"
// in the property form and keeps the section/sidebar numbers sequential.
//
// The property form mixes Impower's React-owned native sections (numbered 1..N) with
// Lux-injected sections that Impower appends at the very bottom. Product wants the
// Technische Anlagen section next to the other maintenance data, i.e. just above the
// insurance overview. Because the native numbers are React-owned they can be
// re-rendered back to their original values, so a debounced observer re-applies the
// move + renumber whenever the property form changes.
//
// The section *bodies* are the source of truth for ordering: Impower always appends
// new injected sections in a consistent body order (…Aufgaben-Automator, Zählerstände,
// Benachrichtigungseinstellungen, Integrationen). The sidebar *links*, however, can end
// up in a different order and with colliding numbers: the Benachrichtigungseinstellungen
// inject self-pins its link "after the link whose number equals the highest section
// number". If that link happens to be numbered the same as the Zählerstände section, the
// inject finds *itself* as the anchor and never moves after Zählerstände — a stable but
// wrong state. To avoid that we reconcile the injected trailing links back into section
// order and renumber the whole list, so the link numbers always mirror the section
// numbers and no collision can occur.

/**
 * Keys a section body can be matched on. Lux-injected links carry `data-section-id`
 * (= body id); Impower's native links carry no id and only share the title with their
 * body (e.g. body `#BuildingEquipment1` ↔ link "Gebäude №1"), so both are offered.
 */
function taOrderSectionKeys(section) {
  const keys = []
  const id = section.id && section.id !== 'undefined' ? section.id : ''
  if (id) keys.push('id:' + id)
  const title =
    section.querySelector('.section-title')?.textContent.trim() || ''
  if (title) keys.push('title:' + title)
  return keys
}

function taOrderLinkKeys(link) {
  const keys = []
  const id = link.dataset.sectionId || ''
  if (id) keys.push('id:' + id)
  const title =
    link
      .querySelector('.link-title')
      ?.textContent.trim()
      .replace(/^\d+(\.\d+)?\s+/, '')
      .trim() || ''
  if (title) keys.push('title:' + title)
  return keys
}

/** Indices of one longest strictly increasing subsequence of `values` (O(n²), n ≈ 15). */
function longestIncreasingIndices(values) {
  const bestLength = values.map(() => 1)
  const previous = values.map(() => -1)
  let end = 0
  for (let i = 0; i < values.length; i++) {
    for (let j = 0; j < i; j++) {
      if (values[j] < values[i] && bestLength[j] + 1 > bestLength[i]) {
        bestLength[i] = bestLength[j] + 1
        previous[i] = j
      }
    }
    if (bestLength[i] > bestLength[end]) end = i
  }
  const indices = []
  for (let i = end; i >= 0 && values.length; i = previous[i]) indices.push(i)
  return indices.reverse()
}

/**
 * Moves the Technische Anlagen section directly above the insurance overview, brings
 * the sidebar links into section-body order (which places the Technische Anlagen link
 * above Versicherungen), then renumbers the form sequentially. Idempotent: it only
 * mutates the DOM when something is actually out of place.
 */
function enforceTechnicalAssetsSectionOrder() {
  const taSection = document.getElementById('TechnicalAssetsSection')
  // The insurance overview owns this slot; fall back to the hidden native section
  // (#ServiceProvider) before the Lux overview has mounted.
  const insuranceSection =
    document.getElementById('LuxInsuranceSection') ||
    document.getElementById('ServiceProvider')
  if (!taSection || !insuranceSection) return false

  // 1) Section body: put Technische Anlagen directly before the insurance section.
  if (
    insuranceSection.previousElementSibling !== taSection &&
    insuranceSection.parentElement
  ) {
    insuranceSection.parentElement.insertBefore(taSection, insuranceSection)
  }

  // 2) Sidebar: mirror the body order, then renumber.
  const sidebar = document.querySelector('.FormSidebar')
  if (sidebar) {
    reconcileSidebarLinkOrder(sidebar)
    renumberPropertySidebar(sidebar)
  }

  renumberPropertySections()
  return true
}

/**
 * Keeps every sidebar link that belongs to a visible section body (native sections,
 * Technische Anlagen, Versicherungen, Zählerstände, Benachrichtigungseinstellungen,
 * Integrationen, …) in the same relative order as those bodies. The Technische Anlagen
 * link is ordered like any other, so it ends up exactly where its body is: directly
 * above Versicherungen.
 *
 * Only links that are actually out of order move (normally just the Technische Anlagen
 * link); links already in body order and links that cannot be matched to a visible body
 * (hidden native sections, or titles that differ from the body) stay where they are, so a
 * partial match can never drag the matched links past unmatched ones.
 */
function reconcileSidebarLinkOrder(sidebar) {
  const sectionKeys = [...document.querySelectorAll('.FormSection')]
    .filter((s) => getComputedStyle(s).display !== 'none')
    .map(taOrderSectionKeys)
  const rankOf = (link) => {
    const keys = taOrderLinkKeys(link)
    return sectionKeys.findIndex((bodyKeys) =>
      keys.some((key) => bodyKeys.includes(key))
    )
  }

  const ranked = [...sidebar.querySelectorAll('.Link.main-link')]
    .map((link) => ({ link, rank: rankOf(link) }))
    .filter((entry) => entry.rank >= 0)
  if (ranked.length < 2) return

  // Links already in ascending body order are anchors; a displaced link is placed
  // directly before the first anchor whose body comes later (so the Technische Anlagen
  // link lands immediately above Versicherungen even when unmatched native links sit
  // in between), or after the last anchor when none does.
  const keep = new Set(longestIncreasingIndices(ranked.map((e) => e.rank)))
  if (keep.size === ranked.length) return
  const anchors = ranked.filter((_, i) => keep.has(i))
  ranked.forEach((entry, i) => {
    if (keep.has(i)) return
    const slot = anchors.findIndex((anchor) => anchor.rank > entry.rank)
    if (slot === -1) {
      anchors[anchors.length - 1].link.after(entry.link)
      anchors.push(entry)
    } else {
      anchors[slot].link.before(entry.link)
      anchors.splice(slot, 0, entry)
    }
  })
}

/** Sequential integers for every top-level sidebar link (sub-links like "4.1" are left alone). */
function renumberPropertySidebar(sidebar) {
  let counter = 0
  for (const link of sidebar.querySelectorAll('.Link.main-link')) {
    if (getComputedStyle(link).display === 'none') continue
    const titleEl = link.querySelector('.link-title')
    if (!titleEl) continue
    const html = titleEl.innerHTML
    const match = html.match(/^(\d+)(\.\d+)?/)
    if (!match || match[2]) continue // no leading number, or a sub-number like "4.1"
    counter += 1
    const next = html.replace(/^\d+/, String(counter))
    if (next !== html) titleEl.innerHTML = next
  }
}

/** Sequential integers for every numbered section; keeps the hidden native insurance number in sync. */
function renumberPropertySections() {
  let counter = 0
  for (const section of document.querySelectorAll('.FormSection')) {
    if (getComputedStyle(section).display === 'none') continue
    const numberEl = section.querySelector('.section-number')
    if (!numberEl) continue
    const text = numberEl.textContent.trim()
    const match = text.match(/^(\d+)(\.\d+)?$/)
    if (!match || match[2]) continue // empty, non-numeric, or a sub-number like "4.1"
    counter += 1
    if (text !== String(counter)) numberEl.textContent = String(counter)
  }
  // The native insurance section is hidden but still carries a number; mirror the overview's
  // so it stays consistent if Impower ever reveals it again.
  const nativeInsurance = document.getElementById('ServiceProvider')
  const luxInsurance = document.getElementById('LuxInsuranceSection')
  if (nativeInsurance && luxInsurance) {
    const luxNumber = luxInsurance.querySelector('.section-number')
    const nativeNumber = nativeInsurance.querySelector('.section-number')
    if (
      luxNumber &&
      nativeNumber &&
      nativeNumber.textContent.trim() !== luxNumber.textContent.trim()
    ) {
      nativeNumber.textContent = luxNumber.textContent.trim()
    }
  }
}

/**
 * Runs one ordering pass immediately and re-applies it (coalesced into an animation
 * frame) whenever the property form re-renders. Safe to call on every property page
 * injection: a previous observer is disconnected first.
 */
function cleanupTechnicalAssetsSectionOrder() {
  if (window.__impowerxTaOrderObserver) {
    window.__impowerxTaOrderObserver.disconnect()
    window.__impowerxTaOrderObserver = null
  }
  if (window.__impowerxTaOrderFrame) {
    cancelAnimationFrame(window.__impowerxTaOrderFrame)
    window.__impowerxTaOrderFrame = 0
  }
}

function initTechnicalAssetsSectionOrder() {
  cleanupTechnicalAssetsSectionOrder()
  const run = () => {
    window.__impowerxTaOrderFrame = 0
    enforceTechnicalAssetsSectionOrder()
  }
  const schedule = () => {
    if (window.__impowerxTaOrderFrame) return
    window.__impowerxTaOrderFrame = requestAnimationFrame(run)
  }
  schedule()
  const observer = new MutationObserver(schedule)
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
  })
  window.__impowerxTaOrderObserver = observer
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    cleanupTechnicalAssetsSectionOrder,
    enforceTechnicalAssetsSectionOrder,
    initTechnicalAssetsSectionOrder,
  }
}
